import Logger from "@cappelendamm/logger";
import { GetShapeBySku } from "../../product/queries/getShapeBySku.catalogue.js";
import { isSubscriptionShapeBookClub } from "../../subscription-plan/selectors/isSubscriptionShape.js";
import { getShopTopicPaths } from "../../subscription-product/selectors/getSubscriptionTopics.js";
const fetchProductInfoBySku = async (skus, { client }) => {
    if (!skus.length)
        return new Map();
    try {
        const data = await client.catalogue.request(GetShapeBySku, { skus });
        return new Map(data.productVariants.map((variant) => {
            const shapeIdentifier = variant?.product?.shapeIdentifier ?? null;
            const clubCode = variant?.product?.clubCode?.content;
            // Series and book subscriptions have a club code too, but theirs is
            // not the name members know them by
            const clubName = shapeIdentifier &&
                isSubscriptionShapeBookClub(shapeIdentifier) &&
                clubCode?.__typename === "SelectionContent"
                ? clubCode.options?.[0]?.key?.trim()
                : undefined;
            return [
                variant?.sku,
                {
                    shapeIdentifier,
                    clubName: clubName || null,
                    shopTopicPaths: getShopTopicPaths(variant?.product?.topics),
                },
            ];
        }));
    }
    catch (error) {
        Logger.error("Error fetching shapes for subscription contracts:", error);
        return new Map();
    }
};
/**
 * Adds the product shape (e.g. "bokklubb", "seriekampanje", "abonnement") of
 * each contract's item. Contracts only know their sku and plan, and the plan
 * does not distinguish book clubs from regular subscriptions.
 * `shapeIdentifier` is null when the sku is not found in the catalogue or the
 * catalogue request fails, so callers can fall back without breaking.
 *
 * Also adds the book club's name from its club code. One club can be split
 * into several products (Barnas Bokverden has one per age group), and the
 * contract only stores the product name, e.g. "1-2 år". `clubName` is null
 * for contracts that are not book clubs.
 *
 * `shopTopicPaths` are the Crystallize paths of the product's shop topics,
 * e.g. "/nettbutikker/krimklubben": the way from a membership to the club's
 * own site. Empty when the sku is not found.
 */
export const getSubscriptionContractsWithShape = async (contracts, { client }) => {
    const skus = [...new Set(contracts.map((contract) => contract.item.sku))];
    const productInfoBySku = await fetchProductInfoBySku(skus, { client });
    return contracts.map((contract) => {
        const productInfo = productInfoBySku.get(contract.item.sku);
        return {
            ...contract,
            shapeIdentifier: productInfo?.shapeIdentifier ?? null,
            clubName: productInfo?.clubName ?? null,
            shopTopicPaths: productInfo?.shopTopicPaths ?? [],
        };
    });
};
